import { useState, useEffect, useMemo } from "react";
import { Workflow, Trash2, Eye, BookOpen, LayoutGrid, List, Loader2, Cpu, LayoutTemplate } from "lucide-react";
import TemplateGalleryModal from "./components/TemplateGalleryModal";
import ConfirmationDialog from "../widgets/ConfirmationModal";
import Tooltip from "../widgets/ToolTip";
import apiClient from "../api/api";
import { API_ENDPOINTS } from "../api/api.config";
import Loader from "../widgets/Loader";
import { toast } from "react-hot-toast";
import TokenAnalyticsDashboard from "./common/panels/TokenAnalyticsDashboard";
import Can from "../components/auth/Can";

const WorkflowListPage = () => {
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [filteredWorkflows, setFilteredWorkflows] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [togglingWorkflowId, setTogglingWorkflowId] = useState<string | null>(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<any>(null);
  const [showTokenAnalytics, setShowTokenAnalytics] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  // ✅ Load workflows via API call
  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        setLoading(true);
        const response = await apiClient.post(
          API_ENDPOINTS.WORKFLOW.WORKFLOW_LIST,
          {}
        );

        if (response.data?.success && response.data?.data?.enhanced_template) {
          const data = response.data.data.enhanced_template;
          setWorkflows(data);
          setFilteredWorkflows(data);
        } else {
          toast.error("⚠️ Failed to fetch workflows.");
        }
      } catch (err: any) {
        console.error("❌ Workflow fetch failed:", err);
        toast.error("❌ Failed to load workflows. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflows();
  }, []);

  // ✅ Search filter
  useEffect(() => {
    let result = [...workflows];

    if (searchTerm.trim()) {
      result = result.filter((wf) =>
        wf.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredWorkflows(result);
  }, [searchTerm, workflows]);

  // ✅ Toggle Workflow Status
  const handleToggleActive = async (workflow: any, newStatus: boolean) => {
    try {
      setTogglingWorkflowId(workflow._id);
      // Fetch full workflow details first to avoid losing nodes/edges on update
      const fetchResponse = await apiClient.get(
        `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflow._id}`
      );
      
      if (!fetchResponse.data?.success || !fetchResponse.data?.data) {
        toast.error("⚠️ Failed to fetch workflow details for update.");
        return;
      }
      
      const fullWorkflow = fetchResponse.data.data;
      const payload = {
        ...fullWorkflow,
        is_active: newStatus
      };
      
      const response = await apiClient.put(
        `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflow._id}`,
        payload
      );

      if (response.data?.success) {
        setWorkflows((prev) =>
          prev.map((wf) => (wf._id === workflow._id ? { ...wf, is_active: newStatus } : wf))
        );
        toast.success(`Workflow is now ${newStatus ? 'Active' : 'Inactive'}.`);
      } else {
        toast.error("⚠️ Failed to update workflow status.");
      }
    } catch (err: any) {
      console.error("❌ Status update failed:", err);
      toast.error("❌ Error updating workflow status.");
    } finally {
      setTogglingWorkflowId(null);
    }
  };

  // ✅ Delete workflow
  const handleDeleteClick = (workflow: any) => {
    setWorkflowToDelete(workflow);
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setWorkflowToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleConfirmDelete = async () => {
    if (!workflowToDelete) return;

    try {
      setDeleting(true);

      const response = await apiClient.delete(
        API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD + `/${workflowToDelete._id}`
      );

      if (response.data?.success) {
        setWorkflows((prev) =>
          prev.filter((wf) => wf._id !== workflowToDelete._id)
        );
        toast.success("🗑️ Workflow deleted successfully.");
      } else {
        toast.error("⚠️ Failed to delete workflow.");
      }
    } catch (err: any) {
      console.error("❌ Delete failed:", err);
      toast.error("❌ Error deleting workflow.");
    } finally {
      setDeleting(false);
      setWorkflowToDelete(null);
      setShowDeleteConfirm(false);
    }
  };

  const stepDisplayNames: Record<string, string> = {
    "FILE-UPLOAD": "On Document Upload",
    "DOCUMENT-STORE": "DocIQ Trigger",
    "FILE-OCR": "OCR Extraction",
    "FILE-DATABASE": "MongoDB Connector",
    "TRANSFORM": "Transform",
    "WEBHOOK": "Webhook Export",
    "FILE-COMPARE": "Compare Data",
    "HTTP": "HTTP Trigger",
    "HTTP-API": "HTTP Request",
    "ONEDRIVE-CONNECTOR": "OneDrive Trigger",
    "GMAIL_CONNECTOR": "Gmail Trigger",
    "OUTLOOK-CONNECTOR": "Outlook Trigger",
    "PDF": "PDF Generator",
    "ONEDRIVE-UPLOAD": "OneDrive Upload",
    "FOR-EACH": "For Each",
    "SUMMARY": "Summarize",
    "IF-ELSE": "If-Else",
    "SWITCH": "Switch",
    "SMART-PARSE": "Document Parsing",
    "EMAIL-OCR": "Email Parsing",
    "EXCEL": "Export to Excel",
    "WORD": "Export to Word",
    "CODE": "Code Node",
    "CLOUD-VISION": "Cloud OCR",
    "GEMINI-UNDERSTANDING": "Smart Extraction (Gemini)",
    "GMAIL-SEND": "Gmail Send",
    "OUTLOOK-SEND": "Outlook Send",
  };

  const formatStepName = (name: string) =>
    stepDisplayNames[name] || name.replace(/[-_]/g, " ");

  // Map workflow IDs to names for the analytics dashboard
  const workflowNames = useMemo(() => {
    const map: Record<string, string> = {};
    workflows.forEach((wf) => {
      if (wf._id && wf.title) map[wf._id] = wf.title;
    });
    return map;
  }, [workflows]);

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Header Toolbar */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 px-6 py-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Left section */}
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-600">
              <Workflow className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Workflows</h1>
              <p className="text-xs text-slate-600">
                Manage and execute document automation pipelines
              </p>
            </div>
          </div>

          {/* Right section */}
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search workflows..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-48 shadow-sm"
            />

            {/* View Toggle */}
            <div className="flex items-center rounded-lg border border-gray-300 bg-gray-50 p-0.5 shadow-sm">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded-md transition-all duration-200 ${
                  viewMode === "grid"
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                aria-label="Grid view"
                title="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-1.5 rounded-md transition-all duration-200 ${
                  viewMode === "list"
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                aria-label="List view"
                title="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            <button
              onClick={() => setShowTokenAnalytics(true)}
              className="flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 text-sm font-medium px-3 py-2 rounded-md shadow-sm transition-colors"
            >
              <Cpu className="h-4 w-4" /> Token Analytics
            </button>
            <button
              onClick={() => window.open("/docs", "_blank")}
              className="flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 text-sm font-medium px-3 py-2 rounded-md shadow-sm transition-colors"
            >
              <BookOpen className="h-4 w-4" /> Docs
            </button>
            <Can permission="workflows:create">
            <button
              onClick={() => setShowTemplates(true)}
              className="flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 text-sm font-medium px-3 py-2 rounded-md shadow-sm transition-colors"
            >
              <LayoutTemplate className="h-4 w-4" /> Templates
            </button>
            </Can>
            <Can permission="workflows:create">
            <button
              onClick={() => (window.location.href = "/workflow/create")}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md shadow-sm transition-colors"
            >
              + Create Workflow
            </button>
            </Can>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col justify-center items-center h-[60vh] text-slate-600">
          <Loader />
          <p className="text-sm mt-2">Fetching your workflows...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredWorkflows.length === 0 && (
        <div className="text-center mt-16 text-slate-500">
          <Workflow className="h-8 w-8 mx-auto mb-3 text-slate-400" />
          <p className="text-sm">No workflows found</p>
          <Can permission="workflows:create">
          <button
            onClick={() => (window.location.href = "/workflow/create")}
            className="mt-3 text-sm text-blue-600 hover:underline"
          >
            Create your first workflow →
          </button>
          </Can>
        </div>
      )}

      {/* Workflow Cards - Grid View */}
      {!loading && filteredWorkflows.length > 0 && viewMode === "grid" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 py-4 ">
          {filteredWorkflows.map((workflow) => (
            <div
              key={workflow._id}
              className="bg-white rounded-md border border-slate-200 p-5 hover:shadow-lg hover:-translate-y-[2px] transition-all duration-200 ease-out flex flex-col cursor-pointer"
              onClick={() => (window.location.href = `/executions/${workflow._id}`)}
            >
              {/* Title + Status */}
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-900 truncate pr-2">
                  {workflow.title}
                </h3>
                <div 
                  className="flex items-center gap-2 flex-shrink-0" 
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="flex items-center cursor-pointer">
                    <span className="me-2 text-xs font-semibold text-gray-600">
                      {workflow.is_active ? "Active" : "Inactive"}
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={workflow.is_active}
                        onChange={(e) => handleToggleActive(workflow, e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`block w-10 h-5 rounded-full transition-colors duration-300 ease-in-out border ${
                          workflow.is_active 
                            ? "bg-blue-600 border-blue-600 shadow-inner" 
                            : "bg-slate-200 border-slate-300"
                        }`}
                      ></div>
                      <div
                        className={`dot flex items-center justify-center absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform duration-300 ease-in-out shadow-sm ${
                          workflow.is_active ? "translate-x-5" : ""
                        }`}
                      >
                        {togglingWorkflowId === workflow._id && (
                          <Loader2 className="w-2.5 h-2.5 text-blue-600 animate-spin" />
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-slate-500m mb-3 line-clamp-2 h-4">
                {workflow.description || "No description provided."}
              </p>

              {/* Steps */}
              <div className="text-xs mb-5 bg-slate-50 p-3 rounded-md border border-slate-100">
                <p className="font-semibold text-slate-700 mb-1.5 uppercase tracking-wide text-[10px]">
                  Workflow Steps
                </p>
                {workflow.workflow_steps?.length ? (
                  <div className="flex flex-wrap items-center gap-1 text-slate-600">
                    {workflow.workflow_steps.map((step: any, idx: number) => (
                      <span
                        key={idx}
                        className="inline-flex items-center"
                      >
                        <span className="font-medium">{formatStepName(step.Node_type)}</span>
                        {idx < workflow.workflow_steps.length - 1 && (
                          <span className="text-slate-400 mx-1.5">→</span>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="italic text-slate-400">No steps defined</p>
                )}
              </div>

              {/* Actions */}
              <div className="mt-auto flex items-center justify-between pt-3 border-t border-slate-100">
                <div className="flex gap-2">
                  <Can permission="workflows:view_executions">
                  <button
                    className="flex items-center justify-center gap-1.5 text-blue-700 border border-blue-200 bg-blue-50 rounded-md px-3 py-1.5 hover:bg-blue-100 hover:border-blue-300 text-xs font-semibold transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = `/executions/${workflow._id}`;
                    }}
                  >
                    <Eye className="h-3.5 w-3.5" /> View
                  </button>
                  </Can>
                </div>
                <Can permission="workflows:delete">
                <Tooltip content="Delete Workflow">
                  <button
                    className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-md transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(workflow);
                    }}
                    disabled={deleting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </Tooltip>
                </Can>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Workflow List - List View */}
      {!loading && filteredWorkflows.length > 0 && viewMode === "list" && (
        <div className="bg-white border border-slate-200 rounded-md mt-4 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-50">
            <div className="col-span-4">Name</div>
            <div className="col-span-3">Steps</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-1">Actions</div>
            <div className="col-span-2 text-center ml-[60px]">Status</div>
          </div>

          {/* Table Rows */}
          {filteredWorkflows.map((workflow) => (
            <div
              key={workflow._id}
              className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors items-center cursor-pointer"
              onClick={() => (window.location.href = `/executions/${workflow._id}`)}
            >
              {/* Name */}
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-blue-50 flex-shrink-0">
                  <Workflow className="h-4 w-4 text-blue-600" />
                </div>
                <span className="font-medium text-slate-900 truncate text-sm">
                  {workflow.title}
                </span>
              </div>

              {/* Steps */}
              <div className="col-span-3 text-sm text-slate-600 truncate">
                {workflow.workflow_steps?.length ? (
                  <span>
                    {workflow.workflow_steps.map((step: any) => formatStepName(step.Node_type)).join(" → ")}
                  </span>
                ) : (
                  <span className="italic text-slate-400">No steps</span>
                )}
              </div>

              {/* Created */}
              <div className="col-span-2 text-sm text-slate-500">
                {new Date(workflow.createdAt).toLocaleDateString()}
              </div>

              {/* Actions */}
              <div className="col-span-1 flex items-center gap-1">
                <Can permission="workflows:view_executions">
                <Tooltip content="View">
                  <button
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded-md transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = `/executions/${workflow._id}`;
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                </Tooltip>
                </Can>
                <Can permission="workflows:delete">
                <Tooltip content="Delete Workflow">
                  <button
                    className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(workflow);
                    }}
                    disabled={deleting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </Tooltip>
                </Can>
              </div>

              {/* Status */}
              <div className="col-span-2 flex items-center justify-end pr-2">
                <div 
                  className="flex items-center gap-2 flex-shrink-0" 
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="flex items-center cursor-pointer">
                    <span className="me-2 text-xs font-semibold text-gray-600">
                      {workflow.is_active ? "Active" : "Inactive"}
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={workflow.is_active}
                        onChange={(e) => handleToggleActive(workflow, e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`block w-10 h-5 rounded-full transition-colors duration-300 ease-in-out border ${
                          workflow.is_active 
                            ? "bg-blue-600 border-blue-600 shadow-inner" 
                            : "bg-slate-200 border-slate-300"
                        }`}
                      ></div>
                      <div
                        className={`dot flex items-center justify-center absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform duration-300 ease-in-out shadow-sm ${
                          workflow.is_active ? "translate-x-5" : ""
                        }`}
                      >
                        {togglingWorkflowId === workflow._id && (
                          <Loader2 className="w-2.5 h-2.5 text-blue-600 animate-spin" />
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template Gallery */}
      <TemplateGalleryModal isOpen={showTemplates} onClose={() => setShowTemplates(false)} />

      {/* Token Analytics Dashboard */}
      {showTokenAnalytics && (
        <TokenAnalyticsDashboard
          onClose={() => setShowTokenAnalytics(false)}
          workflowNames={workflowNames}
        />
      )}

      {/* Delete Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Workflow"
        confirmLabel={deleting ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        description={`Are you sure you want to delete <strong>${
          workflowToDelete?.title || ""
        }</strong>? This action cannot be undone.`}
        iconName="warning"
      />
    </div>
  );
};

export default WorkflowListPage;